import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PoweredByFooter from "../components/PoweredByFooter";
import { ArrowLeft, User, Lock, Save, Check } from "lucide-react";
import { toast } from "sonner";

export default function Profil({ user, onLogout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [role, setRole] = useState(user?.role || "");
  const [savingName, setSavingName] = useState(false);
  const [nameChanged, setNameChanged] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setName(res.data.name || "");
        setEmail(res.data.email || "");
        setOriginalEmail(res.data.email || "");
        setRole(res.data.role || "");
      } catch (error) {
        console.error("Kunne ikke hente profil", error);
      }
    };
    loadProfile();
  }, [token]);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Navn kan ikke være tomt"); return; }
    setSavingName(true);
    try {
      await axios.put(`${API}/auth/me`, { name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Navn opdateret");
      setNameChanged(true);
      setTimeout(() => setNameChanged(false), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering");
    } finally {
      setSavingName(false);
    }
  };

  const saveEmail = async () => {
    if (!email.trim()) { toast.error("Email kan ikke være tom"); return; }
    if (email.trim().toLowerCase() === originalEmail) { toast.info("Email er ikke ændret"); return; }
    setSavingEmail(true);
    try {
      await axios.put(`${API}/auth/me`, { email: email.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email opdateret! Du skal logge ind med den nye email næste gang.");
      setOriginalEmail(email.trim().toLowerCase());
      setEmailChanged(true);
      setTimeout(() => setEmailChanged(false), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering af email");
    } finally {
      setSavingEmail(false);
    }
  };

  const changePassword = async () => {
    if (!currentPassword) { toast.error("Indtast din nuværende adgangskode"); return; }
    if (!newPassword) { toast.error("Indtast en ny adgangskode"); return; }
    if (newPassword.length < 6) { toast.error("Ny adgangskode skal være mindst 6 tegn"); return; }
    if (newPassword !== confirmPassword) { toast.error("De to adgangskoder er ikke ens"); return; }

    setSavingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Adgangskode ændret!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved ændring af adgangskode");
    } finally {
      setSavingPassword(false);
    }
  };

  const roleLabels = {
    admin: "Administrator",
    admin_assistant: "Admin Assistent",
    "arrangør": "Arrangør",
    leader: "Leder",
    volunteer: "Frivillig"
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="profile-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">Min Profil</h1>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Kontoindstillinger</p>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-xl space-y-8">
        {/* Profiloplysninger */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profiloplysninger
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Navn</Label>
              <div className="flex gap-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dit navn"
                  data-testid="profile-name-input"
                />
                <Button onClick={saveName} disabled={savingName} data-testid="save-name-btn">
                  {nameChanged ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <div className="flex gap-2">
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Din email"
                  type="email"
                  data-testid="profile-email-input"
                />
                <Button onClick={saveEmail} disabled={savingEmail} data-testid="save-email-btn">
                  {emailChanged ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Husk: Du logger ind med denne email</p>
            </div>
            <div>
              <Label>Rolle</Label>
              <div className="mt-1">
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium">
                  {roleLabels[role] || role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Skift adgangskode */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Skift adgangskode
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Nuværende adgangskode</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Din nuværende adgangskode"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label>Ny adgangskode</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindst 6 tegn"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>Bekræft ny adgangskode</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Gentag den nye adgangskode"
                data-testid="confirm-password-input"
              />
            </div>
            <Button onClick={changePassword} disabled={savingPassword} className="w-full" data-testid="change-password-btn">
              <Lock className="h-4 w-4 mr-2" />
              {savingPassword ? "Ændrer..." : "Skift adgangskode"}
            </Button>
          </div>
        </div>
      </main>

      <PoweredByFooter />
    </div>
  );
}
